import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTenantConfig } from '../context/TenantConfigContext'
import VehicleCard from '../components/VehicleCard'
import './VehicleListings.css'

function VehicleListings() {
  const { t } = useTranslation()
  const { vehicles, getComponentKey } = useTenantConfig()
  const listingsKey = getComponentKey('listings') || 'listings-pills'
  const gridCols = listingsKey === 'listings-compact' ? 3 : 4

  const [sort, setSort] = useState('recent')
  const [viewMode, setViewMode] = useState('grid')
  const [searchQuery, setSearchQuery] = useState('')

  const searched = useMemo(() => {
    if (!searchQuery.trim()) return vehicles
    const q = searchQuery.trim().toLowerCase()
    return vehicles.filter(
      (v) =>
        v.title?.toLowerCase().includes(q) ||
        v.titleLine1?.toLowerCase().includes(q) ||
        v.titleLine2?.toLowerCase().includes(q) ||
        v.brand?.toLowerCase().includes(q) ||
        v.model?.toLowerCase().includes(q),
    )
  }, [vehicles, searchQuery])

  const sorted = useMemo(() => {
    const arr = [...searched]
    if (sort === 'priceAsc') arr.sort((a, b) => a.price - b.price)
    else if (sort === 'priceDesc') arr.sort((a, b) => b.price - a.price)
    else if (sort === 'mileage') arr.sort((a, b) => (a.mileage || 0) - (b.mileage || 0))
    return arr
  }, [searched, sort])

  const cardVariant = viewMode === 'list' ? 'list' : 'modern'
  const hasSearch = searchQuery.trim().length > 0

  return (
    <div className={`vehicle-listings vehicle-listings--cols-${gridCols}`}>
      <header className="vehicle-listings__hero">
        <div className="vehicle-listings__hero-glow" aria-hidden />
        <div className="vehicle-listings__hero-inner">
          <span className="vehicle-listings__eyebrow">{t('listings.hero.eyebrow')}</span>
          <h1 className="vehicle-listings__title">{t('listings.bannerTitle')}</h1>
          <p className="vehicle-listings__hero-sub">
            {t('listings.hero.subtitle', { count: vehicles.length })}
          </p>
        </div>
      </header>

      <div className="vehicle-listings__main">
        <div className="vehicle-listings__toolbar">
          <p className="vehicle-listings__count">
            {t('listings.found', { count: sorted.length })}
          </p>

          <div className="vehicle-listings__search">
            <input
              type="search"
              placeholder={t('listings.searchVehicle')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="vehicle-listings__search-input"
              aria-label={t('listings.searchVehicle')}
            />
            {hasSearch ? (
              <button
                type="button"
                className="vehicle-listings__search-clear"
                onClick={() => setSearchQuery('')}
              >
                {t('listings.clearSearch')}
              </button>
            ) : null}
          </div>

          <div className="vehicle-listings__controls">
            <label className="vehicle-listings__sort-wrap">
              <span className="vehicle-listings__sort-label">{t('listings.sortBy')}</span>
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="vehicle-listings__sort"
              >
                <option value="recent">{t('listings.sort.recent')}</option>
                <option value="priceAsc">{t('listings.sort.priceAsc')}</option>
                <option value="priceDesc">{t('listings.sort.priceDesc')}</option>
                <option value="mileage">{t('listings.sort.mileage')}</option>
              </select>
            </label>

            <div className="vehicle-listings__view" role="group" aria-label={t('listings.viewMode')}>
              <button
                type="button"
                className={`vehicle-listings__view-btn ${viewMode === 'grid' ? 'vehicle-listings__view-btn--active' : ''}`}
                onClick={() => setViewMode('grid')}
                title={t('listings.viewGrid')}
                aria-pressed={viewMode === 'grid'}
              >
                ▦
              </button>
              <button
                type="button"
                className={`vehicle-listings__view-btn ${viewMode === 'list' ? 'vehicle-listings__view-btn--active' : ''}`}
                onClick={() => setViewMode('list')}
                title={t('listings.viewList')}
                aria-pressed={viewMode === 'list'}
              >
                ☰
              </button>
            </div>
          </div>
        </div>

        {sorted.length === 0 ? (
          <div className="vehicle-listings__empty">
            <span className="vehicle-listings__empty-icon" aria-hidden>
              ◇
            </span>
            <p>{hasSearch ? t('listings.noResultsSearch') : t('listings.noResults')}</p>
            {hasSearch ? (
              <button
                type="button"
                className="vehicle-listings__empty-btn"
                onClick={() => setSearchCode('')}
              >
                {t('listings.clearSearch')}
              </button>
            ) : null}
          </div>
        ) : (
          <div
            className={`vehicle-listings__grid vehicle-listings__grid--cols-${gridCols} vehicle-listings__grid--${viewMode}`}
          >
            {sorted.map((v) => (
              <VehicleCard key={v.id} vehicle={v} variant={cardVariant} />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default VehicleListings
